import { Injectable } from "@angular/core";
import { BillingPeriod, Duration, Phase, PhaseCycle, Plan, PlanComponent, PlanComponentTypes, PlanTypes } from "@asset-owner-portal/api/plan";

@Injectable({
    providedIn: "root",
})
export class PlanToStateService {
    public buildStateFromPlan(plan: Plan): Plan {
        plan.state = {};
        const phase_cycle = this.enumKey(PhaseCycle, PhaseCycle.FINAL);

        let phaseObj: Phase = {
            phase_cycle,
            duration: {
                duration_unit: this.enumKey(Duration, Duration.UNLIMITED),
            },
            plan_components: []
        }

        phaseObj = plan.phases.filter((item) => item.phase_cycle === phase_cycle)[0];
        const plan_components: PlanComponent[] = phaseObj.plan_components;

        let plan_component_type = undefined;
        for (let i = 0; i < plan_components.length; i++) {
            if (Object.keys(plan_components[i][PlanComponentTypes.usage_based_only]).length > 0) {
                plan_component_type = PlanComponentTypes.usage_based_only;
                break;
            }
        }
        if (plan_component_type === PlanComponentTypes.usage_based_only) {
            plan = this.stateForUsageBasedPlan(plan, plan_components);
        } else {
            plan = this.stateForTimeBoundOnly(plan, plan_components);
        }

        return plan;
    }

    private stateForUsageBasedPlan(plan: Plan, plan_components: PlanComponent[]): Plan {
        plan.state['q1'] = PlanTypes.MEASURABLE;
        plan.state['q2'] = [];
        plan.state['q3'] = [];
        plan.state['q4'] = { pay_per_unit: true };
        plan.state['q5'] = { 'plan_components': [] };
        plan.state['q6'] = {};

        for (let i = 0; i < plan_components.length; i++) {
            let planComponentObj: any;
            if (Object.keys(plan_components[i][PlanComponentTypes.usage_based_only]).length > 0) {
                const isCapacity = Object.keys(plan_components[i][PlanComponentTypes.usage_based_only]['capacity']).length > 0;
                planComponentObj = isCapacity
                    ? plan_components[i][PlanComponentTypes.usage_based_only]['capacity']
                    : plan_components[i][PlanComponentTypes.usage_based_only]['consumption'];
                planComponentObj['isRecurring'] = false;
            } else {
                planComponentObj = Object.keys(plan_components[i][PlanComponentTypes.one_time]).length > 0
                    ? plan_components[i][PlanComponentTypes.one_time]
                    : plan_components[i][PlanComponentTypes.time_based_only];
                planComponentObj['isRecurring'] = true;
            }

            if (plan.state.q1 === PlanTypes.MEASURABLE && planComponentObj.isRecurring) {
                plan.state.q1 = PlanTypes.TIME_BOUND_AND_MEASURABLE;
            }

            if (planComponentObj.isRecurring) {
                plan.state.q3.push({ unit: planComponentObj.name });
                plan.state.q5.plan_components.push(this.getTimeBoundPlanComponentObj(planComponentObj, true));
            } else {
                plan.state.q2.push({ unit: planComponentObj.tiers[0].limits ? planComponentObj.tiers[0].limits[0].unit : planComponentObj.tiers[0].limit.unit });
                plan.state.q5['multi_tier'] = planComponentObj.tiers.length > 1;
                plan.state.q5['billing_period'] = planComponentObj.billing_period;
                plan.state.q5['policy'] = planComponentObj.policy;
                plan = this.buildPlanComponents(plan, planComponentObj);
            }
            delete planComponentObj.isRecurring;
        }

        if (plan.state.q5.multi_tier) {
            plan.state.q5.plan_components.sort((a, b) => a.tier - b.tier)
        }

        return plan;
    }

    private buildPlanComponents(plan: Plan, planComponentObj: any): Plan {
        for (let j = 0; j < planComponentObj.tiers.length; j++) {
            if (plan.state.q4.pay_per_unit) {
                plan.state.q4.pay_per_unit = planComponentObj.tiers[j].size ? true : false;
            }

            const limitObj = planComponentObj.tiers[j].limits ? planComponentObj.tiers[j].limits[0] : planComponentObj.tiers[j].limit
            const tierObj = {
                tier_name: planComponentObj.tiers[j].tier_name,
                unit: limitObj.unit,
                price: planComponentObj.tiers[j].price,
                isRecurring: planComponentObj.isRecurring,
            }
            if (plan.state.q5.multi_tier) {
                tierObj['tier'] = j + 1;
                tierObj['from_unit'] = j === 0 ? 0 : (planComponentObj.tiers[j - 1].limits ? planComponentObj.tiers[j - 1].limits[0].max_value + 1 : planComponentObj.tiers[j - 1].limit.max_value + 1);
                tierObj['to_unit'] = limitObj.max_value;
            } else {
                tierObj['size'] = plan.state.q4.pay_per_unit ? planComponentObj.tiers[j].size : limitObj.max_value;
            }

            plan.state.q5.plan_components.push(tierObj);
        }
        return plan;
    }

    private stateForTimeBoundOnly(plan: Plan, plan_components: PlanComponent[]): Plan {
        plan.state['q1'] = PlanTypes.TIME_BOUND;
        plan.state['q2'] = [];
        for (let i = 0; i < plan_components.length; i++) {
            const planComponentObj =
                Object.keys(plan_components[i][PlanComponentTypes.one_time]).length > 0
                    ? plan_components[i][PlanComponentTypes.one_time]
                    : plan_components[i][PlanComponentTypes.time_based_only];
            plan.state.q2.push(this.getTimeBoundPlanComponentObj(planComponentObj));
        }
        return plan;
    }

    private getTimeBoundPlanComponentObj(planComponentObj: any, isRecurring?: boolean): any {
        return {
            unit: planComponentObj.name,
            price: planComponentObj.price,
            billing_period: BillingPeriod[planComponentObj.billing_period] || BillingPeriod[this.enumKey(BillingPeriod, BillingPeriod.NO_BILLING_PERIOD)],
            isRecurring
        }
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    private enumKey(enumObj: Object, value: string): any {
        return Object.keys(enumObj)[Object.values(enumObj).indexOf(value)];
    }
}
